/*
 * FICHIER: MainLayout.js
 * BUT: Layout principal qui structure toute l'application
 * 
 * Ce composant définit la structure globale de l'application :
 * - Header/Navigation en haut
 * - Contenu principal au centre (qui change selon la page)
 * - Footer en bas
 * - Éléments flottants (bouton sondage)
 * - Gestion des animations d'apparition
 * - Méta-données globales (titre, polices, etc.)
 * 
 * Utilisé par React Router comme layout parent pour toutes les pages
 */

// Import des hooks React
import React, { useEffect } from 'react';

// Import de React Router
import { Outlet } from 'react-router-dom'; // Outlet = emplacement où s'affichent les pages enfant

// Import des composants globaux et layout
import GlobalStyles from '../components/common/GlobalStyles';              // Styles CSS globaux
import { Helmet } from 'react-helmet-async';                              // Gestion des meta-données HTML
import Navbar from '../components/layout/Navbar';                         // Barre de navigation
import Footer from '../components/layout/Footer';                         // Pied de page
import FloatingSondageButton from '../components/layout/FloatingSondageButton'; // Bouton flottant sondage
import MainBackgroundWrapper from '../components/layout/MainBackgroundWrapper'; // Wrapper de fond

const MainLayout = () => {
  // EFFET POUR GÉRER LES ANIMATIONS D'APPARITION
  useEffect(() => {
    // Intersection Observer API pour détecter quand un élément devient visible
    // C'est plus performant que d'écouter l'événement scroll
    const observer = new IntersectionObserver((entries) => {
      // Pour chaque élément observé
      entries.forEach(entry => {
        // Si l'élément est visible dans la fenêtre
        if (entry.isIntersecting) {
          // Ajouter la classe CSS 'appear' qui déclenche l'animation
          entry.target.classList.add('appear');
        }
      });
    }, { 
      threshold: 0.15  // Déclencher quand 15% de l'élément est visible
    });
    
    // Observer tous les éléments avec la classe 'appear-animation'
    document.querySelectorAll('.appear-animation').forEach(item => {
      observer.observe(item);
    });
    
    // FONCTION DE NETTOYAGE
    // Quand le composant se démonte, arrêter d'observer pour éviter les fuites mémoire
    return () => {
      document.querySelectorAll('.appear-animation').forEach(item => {
        observer.unobserve(item);
      });
    };
  }, []); // Tableau vide = ne s'exécute qu'une fois au montage
  
  return (
    <>
      {/* HELMET - Gestion des méta-données de la page */}
      <Helmet>
        {/* Import des polices Google Fonts */}
        <link 
          href="https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&family=Poppins:wght@400;500;600;700;800&display=swap" 
          rel="stylesheet" 
        />
        {/* Titre principal affiché dans l'onglet du navigateur */}
        <title>OFFISPORT - Office des Sports de la région de Compiègne</title>
        {/* Description pour le SEO (référencement) */}
        <meta name="description" content="Découvrez les activités sportives, événements et clubs dans la région de Compiègne - OFFISPORT" />
      </Helmet>
      
      {/* APPLICATION DES STYLES GLOBAUX */}
      <GlobalStyles />
      
      {/* STRUCTURE PRINCIPALE DE L'APPLICATION */}
      <div className="App">
        
        {/* BARRE DE NAVIGATION - Toujours visible en haut */}
        <Navbar />
        
        {/* WRAPPER DE FOND PRINCIPAL */}
        <MainBackgroundWrapper>
          {/* CONTENU PRINCIPAL - Change selon la page visitée */}
          <main>
            {/* 
              OUTLET = Point d'insertion des pages enfant 
              C'est ici que s'affichera le contenu de chaque page 
              (Accueil, Actualités, Clubs, etc.)
            */}
            <Outlet />
          </main>
        </MainBackgroundWrapper>
        
        {/* BOUTON FLOTTANT POUR LES SONDAGES - Flotte au-dessus du contenu */}
        <FloatingSondageButton />
        
        {/* PIED DE PAGE - Toujours visible en bas */}
        <Footer />
        
      </div>
    </>
  );
};

// Export du composant pour qu'il puisse être importé dans App.js
export default MainLayout; 